import { VaultCryptoService } from '../../services/vault-crypto.service.js';
import { VaultStoreService } from '../../services/vault-store.service.js';
import { VaultTrustedDeviceService } from '../../services/vault-trusted-device.service.js';
import type { VaultPlainItem } from '../../types/vault.types.js';
import { password } from '@inquirer/prompts';

export interface VaultUnlockOptions {
    passphrase?: string;
    recoveryKey?: string;
}

export function createVaultPassphrasePromptConfig(message: string): { message: string; mask: string } {
    return { message, mask: '*' };
}

export async function resolvePassphrase(options: VaultUnlockOptions): Promise<string | undefined> {
    const existing = options.passphrase || process.env.JAI1_VAULT_PASSPHRASE;
    if (existing) return existing;
    if (!process.stdin.isTTY) return undefined;
    return password(createVaultPassphrasePromptConfig('Vault passphrase:'));
}

export async function unlockVault(options: VaultUnlockOptions = {}): Promise<{
    store: VaultStoreService;
    crypto: VaultCryptoService;
    vaultId: string;
    vmk: Buffer;
}> {
    const store = new VaultStoreService();
    if (!(await store.exists())) {
        throw new Error('Vault is not initialized. Run `j vault init` first.');
    }

    const crypto = new VaultCryptoService();
    const keyring = await store.loadKeyring();
    const recoveryKey = options.recoveryKey || process.env.JAI1_VAULT_RECOVERY_KEY;
    if (recoveryKey) {
        const unlocked = await crypto.unlockWithRecoveryKey(keyring, recoveryKey);
        return { store, crypto, ...unlocked };
    }

    const trustedDeviceUnlock = await new VaultTrustedDeviceService(store.getRootDir())
        .unlock(keyring, await store.loadDevice());
    if (trustedDeviceUnlock) {
        return { store, crypto, ...trustedDeviceUnlock };
    }

    const passphrase = await resolvePassphrase(options);
    if (!passphrase) {
        throw new Error('Vault passphrase required. Use --passphrase or JAI1_VAULT_PASSPHRASE.');
    }

    const unlocked = await crypto.unlockWithPassphrase(keyring, passphrase);
    return { store, crypto, ...unlocked };
}

export function parseFields(fields: string[] = []): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    for (const field of fields) {
        const eqIndex = field.indexOf('=');
        if (eqIndex === -1) {
            throw new Error(`Invalid field "${field}". Use key=value.`);
        }
        const key = field.slice(0, eqIndex).trim();
        const value = field.slice(eqIndex + 1).trim();
        if (!key) throw new Error(`Invalid field "${field}". Key is empty.`);
        if (/^\d+$/.test(value)) body[key] = Number(value);
        else body[key] = value;
    }
    return body;
}

export function printJsonOrText(useJson: boolean, payload: unknown, text: () => void): void {
    if (useJson) {
        console.log(JSON.stringify(payload, null, 2));
        return;
    }
    text();
}

export function redactVaultItem<T extends VaultPlainItem>(item: T): T {
    if (item.containsSecret === false) return item;
    return {
        ...item,
        body: typeof item.body === 'string'
            ? '[redacted]'
            : Object.fromEntries(Object.entries(item.body).map(([key, value]) => [
                key,
                /secret|password|token|key|auth/i.test(key) ? '[redacted]' : value,
            ])),
    };
}

export function shouldRequireRevealReason(item: VaultPlainItem, reason?: string): boolean {
    return item.containsSecret !== false && !reason?.trim();
}

export function formatVaultItemForHuman(
    item: VaultPlainItem,
    options: { includeBody?: 'auto' | 'always' | 'never'; reason?: string } = {}
): string {
    const includeBody = options.includeBody ?? 'auto';
    const shouldIncludeBody = includeBody === 'always' || (includeBody === 'auto' && item.containsSecret === false);
    const visibleItem = shouldIncludeBody ? item : redactVaultItem(item);
    const lines = [
        `${visibleItem.title} (${visibleItem.id})`,
        `Type: ${visibleItem.type}`,
        `Sensitivity: ${visibleItem.sensitivity}`,
        `Contains secret: ${visibleItem.containsSecret === false ? 'no' : 'yes'}`,
        `Tags: ${visibleItem.tags.join(', ') || 'none'}`,
        `Summary: ${visibleItem.summary}`,
    ];

    if (options.reason) lines.push(`Reason: ${options.reason}`);

    if (!shouldIncludeBody) {
        lines.push('', 'Body: [redacted; use reveal --reason "<why>" to view plaintext]');
        return lines.join('\n');
    }

    lines.push('', 'Body', '----', formatVaultBodyForHuman(visibleItem.body));
    return lines.join('\n');
}

export function formatVaultBodyForHuman(body: VaultPlainItem['body']): string {
    if (typeof body === 'string') return body;
    const entries = Object.entries(body);
    if (entries.length === 0) return '(empty)';

    const width = Math.min(
        Math.max(...entries.map(([key]) => key.length), 3),
        28
    );

    return entries.map(([key, value]) => {
        if (value === null || typeof value !== 'object') {
            return `${key.padEnd(width)}  ${String(value)}`;
        }
        return `${key.padEnd(width)}  ${JSON.stringify(value, null, 2)}`;
    }).join('\n');
}
